import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical, Check, X, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import React from "react";
import { useToast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { Textarea } from "@/components/ui/textarea";
import { useApprovedRequestMutation, useRejectedRequestMutation } from "@/services/walletApi";
import { WalletRequest, WalletRequestStatus } from "@/types/wallet";
import warning from "@/assets/warning.svg";
import { Label } from "@/components/ui/label";
import { mintToken } from "@/components/admin/pos/sell";

export function DataTableRowActions({
  data,
  refetch,
  access,
  onViewDetails,
}: {
  data: { id: number; wallet_id: string; request_amount: number; member_id: number } & WalletRequest;
  refetch: () => void;
  access: string;
  onViewDetails: (data: any) => void;
}) {
  const { toast } = useToast();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const jwtToken = localStorage.getItem("userToken") || "";

  // State management
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const [isApproveOpen, setIsApproveOpen] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState("");
  const [isProcessingApproval, setIsProcessingApproval] = React.useState(false);

  // API mutations
  const [approveRequest, { isLoading: isApproving }] =
    useApprovedRequestMutation();
  const [rejectRequest, { isLoading: isRejecting }] =
    useRejectedRequestMutation();

  // Handle approve transaction
  const handleApprove = async () => {
    try {
      setIsProcessingApproval(true);
      
      // Validate wallet_id exists and is not empty
      if (!data.wallet_id || data.wallet_id.trim() === "") {
        toast({
          variant: "destructive",
          title: "Error",
          description:
            "Wallet address is missing. Cannot proceed with approval.",
        });
        return;
      }
      // Validate if it's a valid Ethereum address format
      const ethereumAddressRegex = /^0x[a-fA-F0-9]{40}$/;
      if (!ethereumAddressRegex.test(data.wallet_id)) {
        toast({
          variant: "destructive",
          title: "Error",
          description:
            "Invalid  wallet address format. Please verify the address.",
        });
        return;
      }

      // First call the mintToken API
      const mintResponse = await mintToken({
        user_id: data.member_id,
        transaction_id: 0,
        invoice_id: 0, // Using request ID as invoice_id
        jwt_token: jwtToken,
        org_id: orgId,
        address: data.wallet_id,
        amount: data.request_amount,
        payment_method: "Bank Transfer",
        product_name:"Bank Transfer",
        product_description:"Amount transfered from wallet request",
      });

      // Check if token minting was successful
      if (mintResponse.success) {
        // If minting succeeded, proceed with approving the request
        const resp = await approveRequest({
          id: data.id,
          status: WalletRequestStatus.APPROVED,
          org_id:orgId
        }).unwrap();

        toast({
          variant: "success",
          title: "Transaction Approved",
          description: "The wallet has been topped up successfully.",
        });

        refetch();
        setIsApproveOpen(false);
      } else {
        // If minting failed
        toast({
          variant: "destructive",
          title: "Error",
          description: "Service wallet top up has some issues.",
        });
      }
    } catch (error) {
      console.error("Error approving transaction", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Service wallet top up has some issues.",
      });
    } finally {
      setIsProcessingApproval(false);
    }
  };

  // Handle reject transaction
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please provide a reason for rejection.",
      });
      return;
    }

    try {
      const resp = await rejectRequest({
        id: data.id,
        status: WalletRequestStatus.REJECTED,
        rejection_reason: rejectionReason,
        org_id: orgId,
      }).unwrap();

      toast({
        variant: "success",
        title: "Transaction Rejected",
        description: "The wallet transaction has been rejected.",
      });

      refetch();
      setIsDeleteOpen(false);
      setRejectionReason("");
    } catch (error) {
      console.error("Error rejecting transaction", error);
      handleApiError(error);
    }
  };

  // Error handler
  const handleApiError = (error: any) => {
    if (error && typeof error === "object" && "data" in error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError.data?.detail || typedError.data?.message || "Something went wrong."}`,
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Something went wrong. Please try again.",
      });
    }
  };

  // Handle text input to ensure only single spaces
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // Replace multiple spaces with a single space
    const formattedText = value.replace(/\s+/g, " ");

    // Apply character limit (150 chars)
    if (formattedText.length <= 150) {
      setRejectionReason(formattedText);
    }
  };

  return (
    <>
      {/* Dropdown Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-48"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(data);
            }}
          >
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </DropdownMenuItem>
          {data.status === WalletRequestStatus.PENDING && (
            <>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setIsApproveOpen(true);
                }}
              >
                <Check className="mr-2 h-4 w-4 text-green-500" />
                Approve
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteOpen(true);
                }}
              >
                <X className="mr-2 h-4 w-4 text-red-500" />
                Reject
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Approve Confirmation Dialog */}
      <Dialog open={isApproveOpen} onOpenChange={setIsApproveOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
            <DialogDescription>
              <div className="flex flex-col items-center justify-center gap-4 mt-4">
                <Check className="h-16 w-16 text-green-500" />
                <p className="text-center">
                  Are you sure you want to approve this wallet transaction?
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setIsApproveOpen(false)}
              disabled={isApproving}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={isProcessingApproval}
            >
              {isProcessingApproval ? "Processing..." : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <AlertDialog
        open={isDeleteOpen}
        // onOpenChange={setIsDeleteOpen}
        onOpenChange={(open) => {
          if (open === false && rejectionReason.trim() === "") {
            return;
          }
          setIsDeleteOpen(open);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogDescription>
              <div className="flex flex-col items-center justify-center gap-4">
                <img src={warning} alt="warning" className="w-18 h-18" />
                <AlertDialogTitle className="text-xl font-semibold w-80 text-center">
                  Reject this wallet transaction?
                </AlertDialogTitle>
              </div>
              <div className="my-4">
                <Label htmlFor="reason" className="text-sm font-medium">
                  Rejection Reason
                </Label>
                <Textarea
                  id="reason"
                  className="mt-2"
                  placeholder="Please provide a reason for rejection"
                  value={rejectionReason}
                  onChange={handleTextChange}
                  rows={4}
                  maxLength={150}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {rejectionReason.length}/150 characters
                </div>
              </div>
              <div className="w-full flex justify-between items-center gap-3 mt-4">
                <AlertDialogCancel
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteOpen(false);
                    setRejectionReason("");
                  }}
                  disabled={isRejecting}
                  className="w-full border border-primary font-semibold"
                >
                  <i className="fa fa-xmark text-base px-1"></i>
                  Cancel
                </AlertDialogCancel>
                {/* <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReject();
                  }}
                  disabled={isRejecting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  {isRejecting ? "Rejecting..." : "Reject"}
                </Button> */}
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!rejectionReason.trim()) {
                      toast({
                        variant: "destructive",
                        title: "Error",
                        description: "Please provide a reason for rejection.",
                      });
                      return;
                    }
                    handleReject();
                  }}
                  disabled={isRejecting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  {isRejecting ? "Rejecting..." : "Reject"}
                </Button>{" "}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
